// Вкладка «Обзор»: где объект стоит в иерархии и по каким правилам считается.
//
// Шаблон ведомости подписан происхождением. Объект наследует шаблон от типа объекта, и это
// НЕ то же самое, что свой шаблон: поменяют шаблон у типа — поедут все унаследовавшие. Не
// сказать, откуда взялся шаблон, значит оставить человека гадать, где его править.
//
// Откуда он взялся, решает общая с вкладкой «Ведомость» функция (`site-template.ts`), тремя
// уровнями, как сервер: свой, от типа, по умолчанию. Пока уровней здесь было два, объект на
// шаблоне по умолчанию назывался объектом, по которому «ведомость не построится», — при
// живых ведомостях по нему.

import { RiBriefcaseLine, RiMapPin2Line } from '@remixicon/react';
import { Link } from 'react-router';

import type { SiteOut } from '@/shared/api/endpoints/org';
import { formatDateDMY, parseMoment } from '@/shared/time/format';

import { FieldList, FieldRow } from '@/shared/ui/field-row';
import {
  useContractsQuery,
  useLegalEntitiesQuery,
  usePayrollTemplatesQuery,
} from '@/shared/org/queries';
import { зонаОбъекта } from '../components/ЗонаНаКарте';
import { sitesHref } from '../route';
import type { ШаблонОбъекта } from '../site-template';
import { шаблонОбъекта } from '../site-template';
import { timezoneAsMoscowDiff } from '../timezones';

export function OverviewTab({ site }: { site: SiteOut }) {
  const legalEntities = useLegalEntitiesQuery();
  const contracts = useContractsQuery({ customer_id: site.customer_id });
  const шаблоны = usePayrollTemplatesQuery();

  const legalEntity = legalEntities.data?.items.find((item) => item.id === site.legal_entity_id);
  const contract = contracts.data?.items.find((item) => item.id === site.contract_id);
  const шаблон = шаблонОбъекта(site, шаблоны.data?.items);
  const зона = зонаОбъекта(site);

  return (
    <div className='flex flex-col gap-4 p-5'>
      <FieldList title='Кому принадлежит'>
        <FieldRow
          label='Заказчик'
          value={
            <Link
              to={sitesHref('customers', { filters: {}, panels: [] }, site.customer_id)}
              className='inline-flex items-center gap-1.5 text-primary-base underline тач:inline-flex тач:min-h-11 тач:items-center'
            >
              <RiBriefcaseLine className='size-4' />
              {site.customer_name}
            </Link>
          }
        />
        <FieldRow
          label='Договор'
          // Пока справочник едет — скелет, а не слово «загружаем…»: серый текст неотличим от
          // «договор не привязан», и человек читает ожидание как ответ.
          pending={site.contract_id !== null && contract === undefined && contracts.isPending}
          value={site.contract_id === null ? null : contractTitle(contract, contracts.isPending)}
          missing='договор не привязан: объект работает вне договора'
          note={
            contract === undefined || contract.status === ''
              ? undefined
              : `состояние договора: ${(contract.status_title || contract.status).toLowerCase()}`
          }
        />
        <FieldRow
          label='Юрлицо'
          pending={legalEntity === undefined && legalEntities.isPending}
          value={
            legalEntity === undefined
              ? dictionaryFallback(legalEntities.isPending, 'справочник юрлиц')
              : (legalEntity.short_name ?? legalEntity.name)
          }
        />
        {/* Что даёт тип — сказано ЗДЕСЬ и один раз. Последствие «наследовать неоткуда»
            живёт в строке шаблона ниже, и повторять его тут значит сказать один факт двумя
            разными предложениями на одном экране. */}
        <FieldRow
          label='Тип объекта'
          value={site.site_type_title}
          missing='не задан'
          note='от типа объект наследует шаблон ведомости'
        />
      </FieldList>

      <FieldList title='Где стоит'>
        <FieldRow label='Город' value={site.city} missing='город не указан' />
        <FieldRow label='Регион' value={site.region} missing='регион не указан' />
        <FieldRow
          label='Часовой пояс объекта'
          value={timezoneAsMoscowDiff(site.timezone)}
          note='смена целиком относится к дате заступления по этому поясу, а не по московскому'
        />
        {/* Ссылка «открыть на карте» — вторая половина защиты от перепутанных координат, и без
            неё первая неполна. Рамка правдоподобия на сервере ловит Сургут наоборот (широта
            73 — это Карское море), но Пермь наоборот выглядит нормальной точкой: 56,23/58,01
            вместо 58,01/56,23 уезжает на полтораста километров, и по числам этого не видно.
            Проверить остаётся глазами, и на это должно уходить одно нажатие. */}
        <FieldRow
          label='Точка на карте'
          value={
            зона.точка === null ? null : (
              <span className='inline-flex flex-wrap items-center gap-x-2 gap-y-0.5'>
                {зона.точка}
                {зона.ссылка !== null && (
                  <a
                    href={зона.ссылка}
                    target='_blank'
                    rel='noreferrer'
                    className='inline-flex items-center gap-1 text-primary-base underline тач:inline-flex тач:min-h-11 тач:items-center'
                  >
                    <RiMapPin2Line className='size-4' />
                    открыть на карте
                  </a>
                )}
              </span>
            )
          }
          missing='не задана'
          missingNote='зоны у объекта нет: отметку на посту принимаем без сверки места, и телефон не отметит вход и выход'
          note={зона.точка === null ? undefined : `зона: ${зона.радиус}`}
        />
        <FieldRow
          label='Старший объекта'
          value={site.field_manager_name}
          missing='не назначен'
          missingNote='некому получать уведомления по объекту: просьбы о деньгах и просроченный обход уйдут только по роли'
        />
        <FieldRow
          label='Обход ждут'
          value={
            site.patrol_interval_hours === null
              ? null
              : `не реже чем раз в ${site.patrol_interval_hours} ч`
          }
          missing='обходов здесь не ждут'
          missingNote='сторож про этот объект молчит'
          note={
            site.patrol_interval_hours === null
              ? undefined
              : 'просрочка — повод позвонить, а не нарушение'
          }
        />
      </FieldList>

      <FieldList title='Как по нему считается ведомость'>
        {/* Шаблон называется ПО ИМЕНИ. «Назначен» — это ответ «да» на вопрос «какой»: по нему
            нельзя понять, те ли колонки будут в ведомости этого объекта, а именно за этим сюда
            и заходят. Имя берётся из того же справочника, что и в разделе «Расчёт».
            Пока справочник едет, поле со своим или типовым шаблоном говорит «назначен», а НЕ
            «шаблон не назначен»: справочник на демо-стенде отвечает не мгновенно, и полторы
            секунды карточка утверждала бы ровно обратное тому, что написано в пояснении строкой
            ниже. Когда же своего и типового нет, ответ целиком в справочнике — есть ли шаблон
            по умолчанию, — и до его приезда стоит скелет: ни «по умолчанию», ни «не
            построится» заранее не говорим. */}
        <FieldRow
          label='Шаблон ведомости'
          pending={шаблон.откуда === 'неизвестно' && шаблоны.isPending}
          value={templateTitle(шаблон, шаблоны.error?.status ?? null)}
          missing='шаблон не назначен: ни своего у объекта, ни от типа, ни по умолчанию'
          missingNote='ведомость по объекту не построится, пока шаблон не назначен объекту или его типу'
          note={templateOrigin(шаблон, site)}
        />
        <FieldRow
          label='Состояние'
          value={site.is_active ? 'действует' : 'закрыт'}
          note={
            site.is_active
              ? undefined
              : 'объект не показывается в активных списках, но вся его история сохранена'
          }
        />
      </FieldList>
    </div>
  );
}

function contractTitle(
  contract: { number: string | null; signed_at: string | null } | undefined,
  pending: boolean,
): string {
  if (contract === undefined) {
    return pending ? 'загружаем договор…' : 'договор не нашёлся в списке договоров заказчика';
  }
  const number = contract.number === null ? 'без номера' : `№ ${contract.number}`;
  const signed = parseMoment(contract.signed_at);
  return signed === null ? number : `${number} от ${formatDateDMY(signed)}`;
}

function dictionaryFallback(pending: boolean, subject: string): string {
  return pending ? 'загружаем…' : `${subject} не приехал, покажем название после обновления`;
}

/**
 * Имя действующего шаблона. `null` — шаблона нет ни на одном из трёх уровней, и тогда строка
 * говорит об этом сама вместе со следствием.
 */
function templateTitle(шаблон: ШаблонОбъекта, статусОшибки: number | null): string | null {
  switch (шаблон.откуда) {
    case 'нет':
      return null;
    case 'неизвестно':
      // Скелет стоит, пока справочник едет. Сюда попадаем, только когда он не приехал вовсе:
      // есть ли шаблон по умолчанию, узнать не из чего, и гадать в обе стороны нельзя.
      //
      // Отказ прав — отдельный случай: табельщик карточку объекта видит, а шаблоны ведомости
      // ему не открыты, и «покажем после обновления» было бы обещанием, которое не сбудется.
      return статусОшибки === 403
        ? 'не видно вашей роли: шаблоны ведомости открыты финотделу и руководству'
        : 'справочник шаблонов не приехал: какой действует, покажем после обновления';
    case 'по умолчанию':
      return шаблон.шаблон.title;
    case 'свой':
    case 'тип':
      return шаблон.шаблон?.title ?? 'назначен';
  }
}

/** Откуда у объекта шаблон: свой, от типа или по умолчанию. Разные ответы — разные места правки. */
function templateOrigin(шаблон: ШаблонОбъекта, site: SiteOut): string | undefined {
  switch (шаблон.откуда) {
    case 'свой':
      // Про отсутствие типа здесь НЕ говорим: об этом сказано строкой «Тип объекта» выше, и
      // повторять один факт двумя разными предложениями на одном экране незачем.
      return site.site_type_id === null
        ? 'свой у объекта'
        : 'свой у объекта: шаблон типа объекта на него не влияет';
    case 'тип':
      return `унаследован от типа объекта «${site.site_type_title ?? 'без названия'}»: правка шаблона у типа изменит все такие объекты`;
    case 'по умолчанию':
      return 'шаблон по умолчанию: ни своего у объекта, ни унаследованного от типа';
    case 'нет':
    case 'неизвестно':
      // У «нет» следствие стоит в самой строке, у «неизвестно» объяснять пока нечего.
      return undefined;
  }
}
